import React from 'react';
import { MapPin, Clock, Phone, Globe, Car, Utensils, Heart, ArrowLeft, RotateCcw, Building, Sparkles, Leaf, Shirt, Camera } from 'lucide-react';
import { OptimizedFloralCard } from './ui/OptimizedComponents';
import { FloralErrorBoundary } from './ui/FloralCard';

type Language = 'english' | 'iban';

interface AttendingConfirmationProps {
  guestName: string;
  guestCount: number;
  adultCount?: number;
  childCount?: number;
  invitationType?: 'ceremony-only' | 'ceremony-reception';
  onStartNewRSVP?: () => void;
  selectedLanguage?: Language;
}

const AttendingConfirmation: React.FC<AttendingConfirmationProps> = ({ guestName, guestCount, adultCount, childCount, invitationType = 'ceremony-reception', onStartNewRSVP, selectedLanguage = 'english' }) => {

  // Translation objects
  const translations = {
    english: {
      thankYou: 'Thank You',
      excited: "We're excited to have you",
      joinUs: 'join us on our special day.',
      details: 'HERE ARE ALL THE DETAILS FOR OUR WEDDING:',
      ceremony: 'Wedding Ceremony',
      reception: 'Reception',
      venue: 'Venue',
      importantNotes: 'Important Notes',
      arriveEarly: 'Please arrive 30 minutes early',
      photography: 'Photography is welcome during ceremony. But please do not block the photographer.',
      parking: 'Parking available on-site',
      receptionDetails: 'Reception Details',
      receptionFloor: 'Reception is located on the 1st floor',
      accommodations: 'Accommodations',
      accommodationsDescription: "We've set aside some hotel options nearby for our out-of-town guests, should you wish to book your own stay:",
      dressCode: 'Dress Code',
      dressCodeDescription: 'We would love for our guests to join us in celebrating with these colors:',
      brideColor: 'Bride\'s Side: Green',
      groomColor: 'Groom\'s Side: Blue (Batik preferred)',
      dressCodeNote: 'Feel free to wear any shade of these colors that you\'re comfortable with!',
      dressCodeText: 'Florals, batik on,\nKym and Dom say, "Come and twirl!"—\nIn green, blue, and joy.',
      questions: 'Questions?',
      assistance: "If you have any questions or need assistance, please don't hesitate to reach out:",
      startNewRSVP: 'Start New RSVP',
      uploadPhotos: 'Share Your Photos',
      uploadPhotosDesc: 'Upload and share your wedding photos with us!',
      kilometersFrom: 'kilometers from reception venue',
      and: 'and',
      adult: 'adult',
      adults: 'adults',
      child: 'child',
      children: 'children',

    },
    iban: {
      thankYou: 'Terima Kasih',
      excited: 'Kami enda sabar ngelaluka kita',
      joinUs: 'ngagai hari kami ti ngagaka ati.',
      details: 'TU PENERANG PASAL TIKAH KAMI:',
      ceremony: 'Cheramah Tikah',
      reception: 'Endur Makai',
      venue: 'Endur',
      importantNotes: 'Pengingat',
      arriveEarly: 'Datai tumu kira 30 minit sebedau cheramah berengkah',
      photography: 'Kita dikemendarka ngambi gambar, tang anang mempang sida ke ngambi gambar.',
      parking: 'Bisi endur parking ba endur nya',
      receptionDetails: 'Penerang Pasal Endur Makai',
      receptionFloor: 'Endur nya ba tingkat 1',
      accommodations: 'Endur diau',
      accommodationsDescription: 'Kami bisi ngaga list hotel ke kita ti datai ari endur jauh. Kita tau booking siti ari hotel tu:',
      dressCode: 'Pakaian',
      dressCodeDescription: 'Kami rindu kita ngagai ngambi bahagi enggau kami ngena warna tu:',
      brideColor: 'Pihak Indu: Ijo',
      groomColor: 'Pihak Laki: Biru (Batik lebih dipilih)',
      dressCodeNote: 'Kita bebas ngena warna nya ke nuan senang!',
      dressCodeText: 'Kita tau milih deka ngena gari batik tauka bebungai, ke bechura ijau tauka biru.',
      questions: 'Tanya?',
      assistance: 'Enti nuan bisi tanya tauka begunaka tulung, kita tau terus nanya kami. Tu lumur talipun:',
      startNewRSVP: 'Gaga baru',
      uploadPhotos: 'Kongsi Gambar Nuan',
      uploadPhotosDesc: 'Upload enggau kongsi gambar pengantin nuan enggau kami!',
      kilometersFrom: 'kilometer ari endur makai',
      and: 'enggau',
      adult: 'urang besai',
      adults: 'urang besai',
      child: 'anak',
      children: 'anak-anak',

    }
  };

  const t = translations[selectedLanguage];
  return (
    <FloralErrorBoundary>
      <div className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 relative">
        {/* Corner decorations with flowers */}
        <div className="floral-corner-decoration floral-corner-top-left"></div>
        <div className="floral-corner-decoration floral-corner-top-right"></div>
        <div className="floral-corner-decoration floral-corner-bottom-left"></div>
        <div className="floral-corner-decoration floral-corner-bottom-right"></div>

        <div className="max-w-4xl mx-auto p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8 relative z-10">
          {/* Header */}
          <OptimizedFloralCard variant="elevated" padding="lg" className="text-center" showCornerDecorations={false}>
          <h2 className="floral-title text-2xl sm:text-3xl mb-2">{t.thankYou}, {guestName}!</h2>
          <p className="text-base sm:text-lg text-gray-600 mb-3 sm:mb-4">
            {t.excited}{' '}{t.joinUs}
          </p>
          <div className="floral-divider my-4"></div>
          <p className="floral-subtitle text-sm sm:text-base">{t.details}</p>
          </OptimizedFloralCard>

          {/* Dress Code - Creative Design */}
          <div className="relative overflow-hidden rounded-2xl shadow-xl mb-6">
            {/* Gradient Background */}
            <div className="bg-gradient-to-br from-green-100 via-blue-50 to-green-50 p-8">
              {/* Decorative Elements */}
              <div className="absolute top-0 left-0 w-full h-full opacity-10">
                <div className="absolute top-4 left-4 w-8 h-8 bg-green-400 rounded-full"></div>
                <div className="absolute top-8 right-8 w-6 h-6 bg-blue-400 rounded-full"></div>
                <div className="absolute bottom-6 left-8 w-4 h-4 bg-green-500 rounded-full"></div>
                <div className="absolute bottom-4 right-4 w-10 h-10 bg-blue-300 rounded-full"></div>
                <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-green-600 rounded-full"></div>
                <div className="absolute top-1/3 right-1/3 w-5 h-5 bg-blue-500 rounded-full"></div>
              </div>

              {/* Content */}
              <div className="relative z-10 text-center">
                {/* Header with Icon */}
                <div className="flex justify-center items-center mb-6">
                  <div className="bg-white/80 backdrop-blur-sm p-3 rounded-full shadow-lg mr-3">
                    <Sparkles className="h-6 w-6 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
                    {t.dressCode}
                  </h3>
                </div>

                {/* Poetic Text */}
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/30">
                  <p className="text-gray-800 text-lg sm:text-xl font-medium leading-relaxed whitespace-pre-line italic">
                    {t.dressCodeText}
                  </p>
                </div>

                {/* Color Swatches - 2x2 Grid */}
                <div className="grid grid-cols-2 gap-4 mt-6 max-w-md mx-auto">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full shadow-lg border-2 border-white"></div>
                    <span className="text-sm font-medium text-gray-700">{selectedLanguage === 'iban' ? 'Biru' : 'Blue'}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full shadow-lg border-2 border-white"></div>
                    <span className="text-sm font-medium text-gray-700">{selectedLanguage === 'iban' ? 'Ijau' : 'Green'}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-pink-200 to-rose-300 rounded-full shadow-lg border-2 border-white flex items-center justify-center">
                      <Leaf className="w-3 h-3 text-rose-700" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{selectedLanguage === 'iban' ? 'Bebungai' : 'Florals'}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-amber-200 to-orange-400 rounded-full shadow-lg border-2 border-white flex items-center justify-center">
                      <Shirt className="w-3 h-3 text-amber-700" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{selectedLanguage === 'iban' ? 'Batik' : 'Batik'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Wedding Ceremony */}
          <OptimizedFloralCard variant="elevated" padding="md" showCornerDecorations={false}>
          <div className="flex items-center mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800">{t.ceremony}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">{t.venue}</h4>
              <p className="text-gray-600 mb-1 text-sm sm:text-base">Kingdom Hall of Jehovah's Witnesses</p>
              <p className="text-gray-600 mb-1 text-sm sm:text-base">No 14, Jalan Kandis</p>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">Sibu, 96000</p>

              {/* Navigation Links */}
              <div className="flex flex-col sm:flex-row gap-2 mb-3 sm:mb-4">
                <a
                  href="https://maps.app.goo.gl/CbHn2VRxafSWJyG56"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Google Maps
                </a>
                <a
                  href="https://waze.com/ul?q=Kingdom%20Hall%20of%20Jehovah's%20Witnesses%20No%2014%20Jalan%20Kandis%20Sibu&navigate=yes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Waze
                </a>
              </div>

              <div className="flex items-center text-blue-600 mb-2">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">{selectedLanguage === 'iban' ? '1 November, 2025 jam 10:00 AM' : 'November 1st, 2025 at 10:00 AM'}</span>
              </div>

              <div className="flex items-center text-blue-600">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">(60) 11-2501-8127 (Dominic)</span>
              </div>
            </div>

            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg mt-2 sm:mt-0">
              <h4 className="font-semibold text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">{t.importantNotes}</h4>
              <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                <li>• {t.arriveEarly}</li>
                <li>• {t.photography}</li>
                <li>• {t.parking}</li>
              </ul>
            </div>
          </div>
          </OptimizedFloralCard>

          {/* Reception - Only show for ceremony-reception invitations */}
          {invitationType === 'ceremony-reception' && (
            <OptimizedFloralCard variant="elevated" padding="md" showCornerDecorations={false}>
            <div className="flex items-center mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800">{t.reception}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 text-sm sm:text-base">{t.venue}</h4>
                <p className="text-gray-600 mb-1 text-sm sm:text-base">Royal Wonderful Restaurant</p>
                <p className="text-gray-600 mb-1 text-sm sm:text-base">25-27, Lorong Ulu Sungai Merah 14B</p>
                <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">Pekan Sibu, 96000 Sibu, Sarawak</p>

                {/* Navigation Links */}
                <div className="flex flex-col sm:flex-row gap-2 mb-3 sm:mb-4">
                  <a
                    href="https://maps.app.goo.gl/burmkdsZqYwcr9qa9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Google Maps
                  </a>
                  <a
                    href="https://waze.com/ul?q=Royal%20Wonderful%20Restaurant%2025-27%20Lorong%20Ulu%20Sungai%20Merah%2014B%20Sibu&navigate=yes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Waze
                  </a>
                </div>

                <div className="flex items-center text-green-600 mb-2">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">{selectedLanguage === 'iban' ? '1 November, 2025 jam 11.30 AM - 02:00 PM' : 'November 1st, 2025 at 11:30 AM - 02:00 PM'}</span>
                </div>

                <div className="flex items-center text-green-600">
                  <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">(60) 11-2501-8127 (Dominic)</span>
                </div>
              </div>

              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg mt-2 sm:mt-0">
                <h4 className="font-semibold text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">{t.receptionDetails}</h4>
                <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
                  <li>• {t.receptionFloor}</li>
                </ul>
              </div>
            </div>
            </OptimizedFloralCard>
          )}

          {/* Hotels for Out-of-Town Guests */}
          <OptimizedFloralCard variant="elevated" padding="md" showCornerDecorations={false}>
          <div className="flex items-center mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
              <Building className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800">{t.accommodations}</h3>
          </div>

          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">{t.accommodationsDescription}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Hotel 1 */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-gray-800 mb-1 sm:mb-2 text-sm sm:text-base">Starlink Hotel</h4>
              <p className="text-gray-600 text-xs sm:text-sm mb-1 sm:mb-2">2C, 13, Lorong Tunku Abdul Rahman 8A, Sibu, 96000 Sibu, Sarawak</p>
              <p className="text-gray-600 text-xs sm:text-sm mb-1 sm:mb-2">5.5 {t.kilometersFrom}</p>

              <div className="flex items-center text-amber-600 mb-1 sm:mb-2">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">(084) 221666</span>
              </div>
            </div>

            {/* Hotel 2 */}
            <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-gray-800 mb-1 sm:mb-2 text-sm sm:text-base">Otel Hotel Sibu</h4>
              <p className="text-gray-600 text-xs sm:text-sm mb-1 sm:mb-2">Sublot 12, Hann's Commercial Centre, Sibu, 96000 Sibu, Sarawak</p>
              <p className="text-gray-600 text-xs sm:text-sm mb-1 sm:mb-2">5.7 {t.kilometersFrom}</p>

              <div className="flex items-center text-amber-600 mb-1 sm:mb-2">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">(084) 251133</span>
              </div>

              <div className="flex items-center text-amber-600 mb-2 sm:mb-3">
                <Globe className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">http://www.otel.com.my/</span>
              </div>
            </div>
          </div>

          </OptimizedFloralCard>

          {/* Contact Information */}
          <OptimizedFloralCard variant="default" padding="md" className="text-center" showCornerDecorations={false}>
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-4">{t.questions}</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
            {t.assistance}
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center text-blue-600">
              <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm">Dominic: (60) 11-2501-8127</span>
            </div>
            <div className="flex items-center text-green-600">
              <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm">Kemberly: (60) 18-311-8431</span>
            </div>
          </div>
          </OptimizedFloralCard>



          {/* Action Buttons */}
          <div className="text-center pt-4 sm:pt-6 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <button
                onClick={() => window.location.href = '?photoUpload=true&eventType=wedding'}
                className="floral-button flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 rounded-xl shadow-lg transition-all w-full sm:w-auto bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
              >
                <Camera className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                {t.uploadPhotos}
              </button>
              {onStartNewRSVP && (
                <button
                  onClick={onStartNewRSVP}
                  className="floral-button flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 rounded-xl shadow-lg transition-all w-full sm:w-auto"
                >
                  <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  {t.startNewRSVP}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </FloralErrorBoundary>
  );
};

export default AttendingConfirmation;
